/**
 * Hook personalizado para gestión de usuarios
 * Sistema Klever Octopus - Betel Natural
 */
import { useState, useEffect, useCallback } from 'react';
import userService from '../services/userService';
import { useDebounce } from './useDebounce';

export const useUsers = (initialParams = {}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Parámetros de filtrado
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    ...initialParams
  });

  // Debounce para búsqueda
  const debouncedSearch = useDebounce(filters.search, 500);

  // Cargar usuarios
  const loadUsers = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = {
        ...filters,
        search: debouncedSearch,
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage,
        ...params
      };

      const response = await userService.getUsers(queryParams);

      if (response.success) {
        setUsers(response.data || []);
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            currentPage: response.pagination.page || 1,
            totalPages: response.pagination.pages || 1,
            totalItems: response.pagination.total || 0,
            itemsPerPage: response.pagination.per_page || 10
          }));
        }
      } else {
        setError(response.message);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Error cargando usuarios');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedSearch, pagination.currentPage, pagination.itemsPerPage]);

  // Crear usuario
  const createUser = async (userData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.createUser(userData);

      if (response.success) {
        await loadUsers(); // Recargar lista
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error creating user:', error);
      const errorResponse = { success: false, message: 'Error creando usuario' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Actualizar usuario
  const updateUser = async (id, userData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.updateUser(id, userData);

      if (response.success) {
        // Actualizar usuario en la lista local
        setUsers(prev => prev.map(user => 
          user.id === id ? { ...user, ...response.data } : user
        ));
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error updating user:', error);
      const errorResponse = { success: false, message: 'Error actualizando usuario' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Eliminar usuario
  const deleteUser = async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.deleteUser(id);

      if (response.success) {
        // Remover usuario de la lista local
        setUsers(prev => prev.filter(user => user.id !== id));
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorResponse = { success: false, message: 'Error eliminando usuario' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Cambiar estado del usuario
  const toggleUserStatus = async (id, isActive) => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.changeUserStatus(id, isActive);

      if (response.success) {
        // Actualizar estado en la lista local
        setUsers(prev => prev.map(user => 
          user.id === id ? { ...user, is_active: isActive } : user
        ));
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error changing user status:', error);
      const errorResponse = { success: false, message: 'Error cambiando estado del usuario' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Cambiar contraseña
  const changePassword = async (id, passwordData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.changeUserPassword(id, passwordData);

      if (!response.success) {
        setError(response.message);
      }

      return response;
    } catch (error) {
      console.error('Error changing password:', error);
      const errorResponse = { success: false, message: 'Error cambiando contraseña' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Actualizar filtros
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    // Resetear a la primera página cuando se cambian los filtros
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, []);

  // Cambiar página
  const changePage = useCallback((page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  }, []);

  // Cambiar items por página
  const changeItemsPerPage = useCallback((itemsPerPage) => {
    setPagination(prev => ({
      ...prev,
      itemsPerPage,
      currentPage: 1 // Resetear a la primera página
    }));
  }, []);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      role: '',
      status: '',
      sort_by: 'created_at',
      sort_order: 'desc'
    });
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, []);

  // Efectos
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return {
    // Estado
    users,
    loading,
    error,
    pagination,
    filters,

    // Acciones
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    changePassword,
    updateFilters,
    changePage,
    changeItemsPerPage,
    clearFilters,
    
    // Utilidades
    setError
  };
};

/**
 * Hook para obtener un usuario específico
 */
export const useUser = (userId) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadUser = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await userService.getUserById(userId);

      if (response.success) {
        setUser(response.data);
      } else {
        setError(response.message);
        setUser(null);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      setError('Error cargando usuario');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  return {
    user,
    loading,
    error,
    loadUser,
    setUser
  };
};

/**
 * Hook para estadísticas de usuarios
 */
export const useUserStats = () => {
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    byRole: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.getUserStats();

      if (response.success) {
        setStats(response.data || {
          total: 0,
          active: 0,
          inactive: 0,
          byRole: []
        });
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error('Error loading user stats:', error);
      setError('Error cargando estadísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return {
    stats,
    loading,
    error,
    loadStats
  };
};

export default useUsers;
